import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
    Skill,
    SKILL_PERCEPTION_DESCRIPTIONS,
    PERCEPTION_GAUGE_SKILL_ICONS,
    PERCEPTION_GAUGE_SKILL_LABELS,
} from '../../../model/ai-onboarding.model';

/** Niveau de perception (1–3) pour une compétence. */
export interface PerceptionGaugeValue {
    skillId: string;
    level: number;
}

const GAUGE_POSITIONS: Record<number, number> = { 1: 0, 2: 50, 3: 100 };
const HANDLE_OFFSETS: Record<number, string> = { 1: '0px', 2: '9px', 3: '18px' };

/**
 * Auto-évaluation multi-compétences sur un seul écran (maquette 3).
 * Remplace la sélection skills + steps perception séparés.
 */
@Component({
    selector: 'lib-ai-exam-onboarding-perception-gauges',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    templateUrl: './AI-exam-onboarding-perception-gauges.component.html',
    styleUrls: ['./AI-exam-onboarding-perception-gauges.component.scss'],
})
export class AIExamOnboardingPerceptionGaugesComponent implements OnInit, OnChanges {
    @Input() skills: Skill[] = [];
    @Input() selectedLanguageName = '';
    /** Valeurs initiales skillId → level (1–3). */
    @Input() initialLevels: Record<string, number> = {};

    @Output() levelsChange = new EventEmitter<PerceptionGaugeValue[]>();
    @Output() skillsSelected = new EventEmitter<string[]>();
    @Output() back = new EventEmitter<void>();
    @Output() continue = new EventEmitter<void>();

    /** Niveaux courants par skill. */
    levels = signal<Record<string, number>>({});

    ngOnInit(): void {
        this.rebuildLevels();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['skills'] && !changes['skills'].firstChange) {
            this.rebuildLevels();
        }
    }

    private rebuildLevels(): void {
        const initial: Record<string, number> = {};
        this.skills.forEach((skill) => {
            initial[skill.id] = this.levels()[skill.id] ?? this.initialLevels[skill.id] ?? 2;
        });
        this.levels.set(initial);
        this.emitChanges();
        if (this.skills.length > 0) {
            this.skillsSelected.emit(this.skills.map((s) => s.id));
        }
    }

    setLevel(skillId: string, level: number): void {
        this.levels.update((current) => ({ ...current, [skillId]: level }));
        this.emitChanges();
    }

    onTrackClick(skillId: string, event: MouseEvent): void {
        const track = event.currentTarget as HTMLElement;
        const rect = track.getBoundingClientRect();
        const pct = (event.clientX - rect.left) / rect.width;
        const level = pct < 0.33 ? 1 : pct < 0.66 ? 2 : 3;
        this.setLevel(skillId, level);
    }

    getFillWidth(skillId: string): string {
        const level = this.levels()[skillId] ?? 2;
        return `${GAUGE_POSITIONS[level]}%`;
    }

    getHandleLeft(skillId: string): string {
        const level = this.levels()[skillId] ?? 2;
        return `calc(${GAUGE_POSITIONS[level]}% - ${HANDLE_OFFSETS[level]})`;
    }

    isLabelActive(skillId: string, level: number): boolean {
        return (this.levels()[skillId] ?? 2) === level;
    }

    descriptionKey(skillId: string, level: number): string {
        return SKILL_PERCEPTION_DESCRIPTIONS[skillId]?.[level] || '';
    }

    /** Clé i18n du libellé compétence (maquette 3). */
    skillLabelKey(skillId: string): string {
        return PERCEPTION_GAUGE_SKILL_LABELS[skillId] || '';
    }

    /** Emoji compétence (style chips comfort). */
    skillIcon(skillId: string): string {
        return PERCEPTION_GAUGE_SKILL_ICONS[skillId] || '';
    }

    private emitChanges(): void {
        const values = Object.entries(this.levels()).map(([skillId, level]) => ({
            skillId,
            level,
        }));
        this.levelsChange.emit(values);
    }
}
